"use client";
import { Link } from "@/i18n/routing";
import { IconBag, IconForwardArrow } from "@repo/assets/images";
import {
  CartCalculations,
  CartItems,
  CurrencySymbol,
  EmptyCart,
  ProceedToCheckoutButton,
  PromoCode,
  ToggleSummary,
} from "@repo/components";
import { CartContext, useCartContext } from "@repo/contexts/CartContext";
import { useViewCart } from "@repo/lib/hooks/gtm";
import { useTrackViewCart } from "@repo/lib/hooks/webengage";
import { OffCanvasDrawer } from "@repo/ui";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { useCallback, useEffect } from "react";

const MiniCart = ({ session, cartTotalsInfo, WishlistProducts, locale }) => {
  const isCheckout = usePathname() === `/${locale}/checkout`;
  const t = useTranslations("Common");
  const tCart = useTranslations("Cart");
  const { showMiniCart, setShowMiniCart, setCartTotalsInfo } =
    useCartContext(CartContext);
  const { lineItems, cartTotal, itemsQuantity, couponName, isAutoApply } =
    cartTotalsInfo || {};
  const isItemAvailable = itemsQuantity > 0;
  const { handleViewCartEvent } = useViewCart();
  const { trackViewCartEvent } = useTrackViewCart();

  const handleMiniCart = useCallback(() => {
    setShowMiniCart(!showMiniCart);
    if (!showMiniCart) {
      // Call the GTM event when the mini cart is opened and items are available
      handleViewCartEvent(cartTotalsInfo);
      cartTotalsInfo?.lineItems?.length > 0 &&
        trackViewCartEvent(cartTotalsInfo);
    }
  }, [
    showMiniCart,
    setShowMiniCart,
    handleViewCartEvent,
    cartTotalsInfo,
    trackViewCartEvent,
  ]);

  const hasOutOfStock = lineItems?.some(
    (item) => !item.isStockStatus || !item.active
  );

  //Save the cart data to context for the other components to use
  useEffect(() => {
    setCartTotalsInfo(cartTotalsInfo);
  }, [cartTotalsInfo, setCartTotalsInfo]);

  return (
    <>
      {isCheckout ? (
        <Link href="/cart" className="relative block pe-2">
          <IconBag className="text-header-icon w-[18px] fill-current md:w-6" />
          {itemsQuantity > 0 && (
            <div className="absolute -end-1 -top-2 size-[20px] rounded-full bg-red-600">
              <div className="flex h-full items-center justify-center text-[11px] font-bold text-white">
                {itemsQuantity}
              </div>
            </div>
          )}
        </Link>
      ) : (
        <>
          <button
            className="relative hidden items-center px-2 sm:flex"
            onClick={handleMiniCart}
            aria-label="Open Cart"
          >
            <IconBag className="text-header-icon w-[18px] fill-current md:w-6" />
            {itemsQuantity > 0 && (
              <div className="absolute -end-1 -top-2 size-[20px] rounded-full bg-red-600">
                <div className="flex h-full items-center justify-center text-[11px] font-bold text-white">
                  {itemsQuantity}
                </div>
              </div>
            )}
          </button>
          <Link
            href="/cart"
            className="relative block px-2 sm:hidden"
            aria-label="View shopping cart"
          >
            <IconBag className="text-header-icon w-[18px] fill-current" />
            {itemsQuantity > 0 && (
              <div className="absolute -end-1 -top-2 size-[20px] rounded-full bg-red-600">
                <div className="flex h-full items-center justify-center text-[11px] font-bold text-white">
                  {itemsQuantity}
                </div>
              </div>
            )}
          </Link>
        </>
      )}
      <OffCanvasDrawer
        open={showMiniCart}
        handleClose={handleMiniCart}
        title={tCart("Cart")}
        position="right"
      >
        {isItemAvailable ? (
          <div>
            <div className="p-4">
              <CartItems
                products={lineItems}
                miniCart={showMiniCart}
                handleClose={handleMiniCart}
                t={t}
              />
            </div>

            <div className="px-4">
              <div className="border-miniCart-checkout-border border">
                <div className="border-miniCart-checkout-border bg-miniCart-checkout-bg border-b">
                  {session ? (
                    <PromoCode
                      coupon={couponName}
                      session={session}
                      isAutoApply={isAutoApply}
                    />
                  ) : (
                    <p className="p-4 text-sm">
                      {t("Please")}{" "}
                      <Link href="/login" className="font-bold">
                        {t("Login")}
                      </Link>{" "}
                      {t("To_Redeem_Your_Coupon")}
                    </p>
                  )}
                </div>
                <ToggleSummary
                  title={tCart("Pricing_Details")}
                  variant="secondary"
                >
                  <CartCalculations
                    summary={cartTotal}
                    couponName={couponName}
                  />
                </ToggleSummary>
                <div className="bg-miniCart-checkout-bg flex p-3">
                  <div className="text-miniCart-text text-lg font-semibold">
                    {tCart("Total")}
                  </div>
                  <div className="text-miniCart-text ms-auto text-lg font-semibold">
                    <CurrencySymbol value={cartTotal?.formattedTotal} />
                  </div>
                </div>
                <div className="bg-miniCart-checkout-bg px-3 pb-4 pt-2">
                  <ProceedToCheckoutButton
                    hasOutOfStock={hasOutOfStock}
                    closeMinicart={handleMiniCart}
                    cart={cartTotalsInfo}
                    locale={locale}
                  />
                </div>
              </div>
            </div>

            <div className="px-6 py-3 text-center">
              <Link
                href="/cart"
                onClick={handleMiniCart}
                className="text-miniCart-link flex items-center justify-center text-base font-semibold"
              >
                {tCart("View_Cart_Details")}{" "}
                <IconForwardArrow className="rtl:rotate-180" />
              </Link>
            </div>
            <div className="px-3">{WishlistProducts}</div>
          </div>
        ) : (
          <>
            <EmptyCart closeMinicart={handleMiniCart} />
            <div className="px-3">{WishlistProducts}</div>
          </>
        )}
      </OffCanvasDrawer>
    </>
  );
};

export default MiniCart;
